import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox } from '@naf-simba/elements-checkbox';
import { useDispatch } from 'react-redux';
import {
  terms as T,
  createElbilguideAPIQuery,
  TRAILER_HITCH_PRESENT_OPTION_NAME,
  useCarFilterOptions,
  useFilterAction,
  useFilterCheckedMap,
} from '@utils/bilguide';
import { FilterType } from '@appTypes/urlParamsType';
import { actions as carFilterActions } from '@redux/modules/carFilter';
import { getURLParams } from '@lib/getUrlParams';
import PrimaryFilterSection from './PrimaryFilterSection';
import SecondaryFilterSection from './SecondaryFilterSection';
import {
  CheckboxWrapper,
  ExpandableListWrapper,
  ExtraFiltersWrapper,
  ShowMoreFiltersWrapper,
  Wrapper,
} from './CarFilter.styles';

const CHASSIS_VISIBLE_DEFAULT = 4;
const CHASSIS_VISIBLE_EXPANDED = 10;

interface CheckboxEvent {
  name: keyof FilterType;
  value: boolean;
}

const CarFilter = () => {
  const dispatch = useDispatch();
  const { applyFilter } = useFilterAction();
  const { allFilters, chassis, categories, trailerHitch, numberOfSeats, drivetrains, brands, isFetchingFilters } =
    useCarFilterOptions();
  const { filter, checkedMap } = useFilterCheckedMap();
  const [chassisVisible, setChassisVisible] = useState(CHASSIS_VISIBLE_DEFAULT);
  const [showExtraMobileFilters, setShowExtraMobileFilters] = useState(false);

  useEffect(() => {
    if (Object.keys(allFilters).length > 0 || isFetchingFilters) return;
    const queries = createElbilguideAPIQuery(getURLParams());
    dispatch(carFilterActions.getCarArguments.request(queries));
  }, [dispatch, allFilters, isFetchingFilters]);

  const handleCheckboxChange = useCallback(
    (filterKey: keyof FilterType) => (e: CheckboxEvent) => applyFilter(filterKey, e.name),
    [applyFilter],
  );

  // Trailer-hitch checkbox dispatches the literal value 'true' (it's a single yes/no option).
  const handleTrailerHitchChange = useCallback((e: CheckboxEvent) => applyFilter(e.name, 'true'), [applyFilter]);

  const handleExpandChassis = useCallback(() => setChassisVisible(CHASSIS_VISIBLE_EXPANDED), []);
  const handleToggleExtraFilters = useCallback(() => setShowExtraMobileFilters((v) => !v), []);

  const withTrailerHitchItem = trailerHitch?.find((item) => item.name === TRAILER_HITCH_PRESENT_OPTION_NAME);

  return (
    <Wrapper>
      <PrimaryFilterSection
        title="Biltype"
        options={chassis}
        checked={checkedMap?.chassis}
        onChange={handleCheckboxChange('chassis')}
        maxVisible={chassisVisible}
        onShowMore={handleExpandChassis}
      />
      <PrimaryFilterSection
        title={T.CATEGORIES}
        options={categories}
        checked={checkedMap?.categories}
        onChange={handleCheckboxChange('categorynames')}
      />
      <ExtraFiltersWrapper $showMobileFilters={showExtraMobileFilters}>
        {!!withTrailerHitchItem && (
          <ExpandableListWrapper>
            <CheckboxWrapper>
              <Checkbox
                key={`trailerhitch-${withTrailerHitchItem.count}`}
                onChange={handleTrailerHitchChange}
                name="trailerHitch"
                label={`Tilhengerfeste (${withTrailerHitchItem.count})`}
                checked={!!checkedMap?.trailerHitch}
              />
            </CheckboxWrapper>
          </ExpandableListWrapper>
        )}
        <SecondaryFilterSection
          title="Merke"
          options={brands}
          checked={checkedMap?.brands}
          onChange={handleCheckboxChange('brands')}
          identifierKey="value"
          openByDefault={typeof filter?.brands !== 'undefined'}
        />
        <SecondaryFilterSection
          title="Antall seter"
          options={numberOfSeats}
          checked={checkedMap?.numberOfSeats}
          onChange={handleCheckboxChange('numberOfSeats')}
          openByDefault={typeof filter?.numberOfSeats !== 'undefined'}
        />
        <SecondaryFilterSection
          title="Drivhjul"
          options={drivetrains}
          checked={checkedMap?.drivetrains}
          onChange={handleCheckboxChange('drivetrains')}
          openByDefault={typeof filter?.drivetrains !== 'undefined'}
        />
      </ExtraFiltersWrapper>
      <ShowMoreFiltersWrapper onClick={handleToggleExtraFilters}>
        {!showExtraMobileFilters ? (
          <>
            &#43; <span>Vis flere filtre</span>
          </>
        ) : (
          <>
            - <span>Lukk filter</span>
          </>
        )}
      </ShowMoreFiltersWrapper>
    </Wrapper>
  );
};

export default CarFilter;
